import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

/**
 * White fill, 1px neutral-200 border, radius 14, and at rest almost no shadow.
 * Depth arrives on hover, not before — pass `hoverable` for rows and cards that
 * lead somewhere, and leave it off for panels that are just containers.
 *
 * Padding is `18px 20px`. The app is not on a 4/8 grid and those numbers are
 * the design system's, not a rounding of `p-5`.
 *
 * The card itself carries no padding so the shadcn sub-parts still compose;
 * pass `padded` for the common case of a card that is simply a padded box.
 */
export const cardVariants = cva(
  "rounded-card border bg-white text-neutral-900 transition-[border-color,box-shadow,transform] duration-200",
  {
    variants: {
      variant: {
        default: "border-neutral-200 shadow-card-rest",
        flat: "border-neutral-200 shadow-none",
        /* Accent panels — the escrow summary, the guarantee band. */
        accent: "border-steel-100 bg-steel-50 shadow-none",
        /* "Not yet real": the upload dropzone, the undo bar. */
        dashed: "border-dashed border-neutral-300 bg-transparent shadow-none",
        /* Kept for the unrouted ADVisor views. */
        outline: "border-neutral-200 shadow-none",
        elevated: "border-neutral-200 shadow-list-hover",
      },
      hoverable: {
        true: "hover:border-neutral-300 hover:shadow-list-hover",
      },
      padded: {
        true: "px-5 py-[18px]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface CardProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, hoverable, padded, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(cardVariants({ variant, hoverable, padded, className }))}
      {...props}
    />
  ),
);
Card.displayName = "Card";

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col gap-1 px-5 pt-[18px] pb-3", className)}
    {...props}
  />
));
CardHeader.displayName = "CardHeader";

const CardTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-card-title-sm text-neutral-900", className)}
    {...props}
  />
));
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("text-fine text-neutral-400", className)} {...props} />
));
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("px-5 pb-[18px]", className)} {...props} />
));
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    // neutral-100 is the divider INSIDE a card. Using the card's own border
    // colour here makes one card read as two.
    className={cn("flex items-center gap-2 border-t border-neutral-100 px-5 py-3", className)}
    {...props}
  />
));
CardFooter.displayName = "CardFooter";

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
};
